import {
  Modal,
  Table,
  Progress,
  Statistic,
  Row,
  Col,
  Alert,
  Skeleton,
  Result,
  Button,
  Divider,
} from "antd";
import { useEffect, useReducer, useState, useContext } from "react";
import {
  autoPackageDeliveryWithoutMessage,
  autoPackagePrintBarcode,
} from "@DELIVERIES";
import useRequestQueue from "../../deliveries/pre-order/table/hooks/request-queue";
import { useHistory } from "react-router-dom";
import { AuthContext } from "@CONTEXTS";
import { b64toBlob, TermalPrint, mapProductsForApi } from "@HELPERS";
import PrintModal from "../../deliveries/operation/table/print-modal";

const initialState = [];
const reducer = (state, action) => {
  const { type, payload } = action;

  switch (type) {
    case "SET_DELIVERIES":
      return payload.map((delivery) => ({
        ...delivery,
        status: "RUNNING",
      }));
    case "UPDATE_DELIVERY_STATUS_SUCCESS":
      return state.map((delivery) => {
        if (delivery.customerDeliveryNo === payload.id) {
          return {
            ...delivery,
            status: "SUCCESS",
          };
        }
        return delivery;
      });
    case "UPDATE_DELIVERY_STATUS_ERROR":
      return state.map((delivery) => {
        if (delivery.customerDeliveryNo === payload.id) {
          return {
            ...delivery,
            status: "ERROR",
            errorMessage: payload.errorMessage,
          };
        }
        return delivery;
      });
    default:
      break;
  }
};

const PackageResultModal = ({ onClose, data, onSuccessAndRemove }) => {
  const [state, dispatch] = useReducer(reducer, initialState);
  const [successCount, setSuccessCount] = useState(0);
  const [errorCount, setErrorCount] = useState(0);
  const [waitingCount, setWaitingCount] = useState(0);
  const [mounted, setMounted] = useState(false);
  const [loading, setLoading] = useState(false);
  const [allSuccess, setAllSuccess] = useState(false);
  const [allDone, setAllDone] = useState(false);
  const [errorList, setErrorList] = useState([]);
  const history = useHistory();
  const [succesBarcodes, setSuccessBarcodes] = useState([]);
  const [printModal, setPrintModal] = useState(false);
  const { info } = useContext(AuthContext);
  const columns = [
    {
      title: "Sipariş numarası",
      dataIndex: "customerDeliveryNo",
      key: "customerDeliveryNo",
      width: "25%",
    },
    {
      title: "Durum",
      dataIndex: "status",
      key: "status",
      width: "50%",
      render: (data, record) => {
        switch (data) {
          case "RUNNING":
            return (
              <Skeleton.Input
                active
                block
                shape="round"
                size="small"
                key={record?.customerDeliveryNo}
              />
            );
          case "SUCCESS":
            return (
              <Progress
                percent={100}
                size="small"
                key={record?.customerDeliveryNo}
              />
            );
          case "ERROR":
            return (
              <Alert
                message={record.errorMessage}
                type="error"
                showIcon
                key={record?.customerDeliveryNo}
              />
            );
          default:
            return "HATA!";
        }
      },
    },
  ];

  const { enqueue } = useRequestQueue(5); // Eş zamanlı maksimum 5 istek

  useEffect(() => {
    dispatch({ type: "SET_DELIVERIES", payload: data });
  }, [data, dispatch]);

  useEffect(() => {
    if (state) {
      const success = state?.filter(
        (delivery) => delivery.status === "SUCCESS"
      );
      const error = state?.filter((delivery) => delivery.status === "ERROR");
      const waiting = state?.filter(
        (delivery) => delivery.status === "RUNNING"
      );
      if (success.length === data.length) {
        setAllSuccess(true);
      }
      setSuccessCount(success.length);
      setErrorCount(error.length);
      setWaitingCount(waiting.length);

      if (waiting.length === 0) {
        setSuccessBarcodes(
          success.map((item) => ({
            customerDeliveryNo: item.customerDeliveryNo,
            totalParcels: item.totalParcels,
          }))
        );
        setLoading(false);
        setAllDone(true);
      }
    }
  }, [data, state, dispatch]);

  const handlePrint = async () => {
    setLoading(true);
    const response = await autoPackagePrintBarcode(succesBarcodes);
    setLoading(false);
    if (response?.pdf) {
      const b64 = b64toBlob(response.pdf, "application/pdf");
      const blobUrl = URL.createObjectURL(b64);
      setPrintModal(blobUrl);
    }
    if (response?.zplArray) {
      TermalPrint(response?.zplArray);
    }
  };

  useEffect(() => {
    if (allDone) {
      const doneDeliveries = [];
      const errorDeliveries = [];
      state.forEach((item, index) => {
        if (item.status === "SUCCESS") {
          doneDeliveries.push(item.customerDeliveryNo);
        } else {
          errorDeliveries.push({
            customerDeliveryNo: item.customerDeliveryNo,
            errorMessage: item.errorMessage,
          });
        }
      });

      if (typeof onSuccessAndRemove === "function") {
        onSuccessAndRemove(doneDeliveries);
      }

      if (errorDeliveries.length > 0) {
        setErrorList(errorDeliveries);
      }
    }
  }, [allDone, state]);

  useEffect(() => {
    if (state && state.length > 0 && !mounted) {
      state.forEach((item) => {
        const formData = new FormData();

        formData.append("customerDeliveryNo", item.customerDeliveryNo || null);

        formData.append("warehouse", item.warehouse || null);
        formData.append("desi", item.desi || null);
        formData.append("manuelDesi", item.manuelDesi || null);
        formData.append("totalParcels", item.totalParcels || null);
        formData.append("deliveryType", item.deliveryType || null);
        if (item.slot) formData.append("slot", item.slot || null);
        formData.append("deliveryAmount", item.deliveryAmount || null);
        formData.append("paymentType", item.paymentType || null);
        formData.append("products", mapProductsForApi(item?.products));
        
        formData.append(
          "recipient",
          JSON.stringify({
            firstName: item.recipientName,
            lastName: item.recipientSurname,
            phone: item.recipientPhone.replace(/ /g, ""),
            address: {
              addressLine: item.recipientAddressLine,
              city: {
                id: item.recipientCity?.id,
                name: item.recipientCity.name,
              },
              town: {
                id: item.recipientTown?.id,
                name: item.recipientTown.name,
              },
              district: item.recipientDistrict
                ? {
                    id: item.recipientDistrict?.id,
                    name: item.recipientDistrict.name,
                  }
                : null,
            },
          }) || null
        );

        if (item.receiverName) {
          formData.append(
            "receiver",
            JSON.stringify({
              firstName: item.receiverName,
              lastName: item.receiverSurname,
              phone: item.receiverPhone.replace(/ /g, ""),
            }) || null
          );
        }

        if (info?.customFields) {
          const customFields = info?.customFields?.filter((x) =>
            x?.deliveryType?.includes("DELIVERY")
          );

          const cfx = [];

          for (let i = 0; i < customFields.length; i++) {
            const customItem = customFields[i];

            cfx.push({
              name: customItem.name,
              value: item[customItem.name],
            });

            if (customItem.type === "FILE_UPLOADER") {
              if (
                (!item[customItem.name] ||
                  item[customItem.name]?.length < 1 ||
                  !item[customItem.name]?.[0]?.originFileObj) &&
                customItem.required
              ) {
                return new Promise((resolve) =>
                  resolve({
                    success: false,
                    message: `${customItem.label} için geçersiz dosya.`,
                  })
                );
              }

              formData.append(
                customItem.name,
                item[customItem.name]?.[0]?.originFileObj
              );
            }
          }

          if (cfx.length > 0) {
            formData.append("customFields", JSON.stringify(cfx));
          }
        }
        enqueue(() => autoPackageDeliveryWithoutMessage(formData))
          .then((response) => {
            if (response.success) {
              dispatch({
                type: "UPDATE_DELIVERY_STATUS_SUCCESS",
                payload: { id: item.customerDeliveryNo },
              });
            } else {
              dispatch({
                type: "UPDATE_DELIVERY_STATUS_ERROR",
                payload: {
                  id: item.customerDeliveryNo,
                  errorMessage:
                    response?.response?.data?.message || "Bir sorun oluştu",
                },
              });
            }
          })
          .catch((error) => {
            console.error(error);
            // Hata işleme
            dispatch({
              type: "UPDATE_DELIVERY_STATUS_ERROR",
              payload: {
                id: item.customerDeliveryNo,
                errorMessage: "Bir sorun oluştu.",
              },
            });
          });
      });
      setMounted(true);
      setLoading(true);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [state]);

  return (
    <>
      {printModal && (
        <PrintModal data={printModal} onClose={() => setPrintModal(false)} />
      )}
      <Modal
        visible
        title="Toplu Gönderi Paketleme Durumu"
        onCancel={() => {
          if (!loading) {
            setSuccessBarcodes([]);
            onClose();
          }
        }}
        width={950}
        footer={null}
      >
        <Row gutter={24}>
          <Col span={8}>
            <Statistic title="Başarılı Gönderi Sayısı" value={successCount} />
          </Col>
          <Col span={8}>
            <Statistic title="Hatalı Gönderi Sayısı" value={errorCount} />
          </Col>
          <Col span={8}>
            <Statistic
              title="Sırada Bekleyen Gönderi Sayısı"
              value={waitingCount}
            />
          </Col>
        </Row>
        <div style={{ marginTop: 24 }}>
          {allSuccess ? (
            <>
              <Divider />
              <Result
                status="success"
                title="Gönderiler Başarıyla Oluşturuldu"
                subTitle={`Gönderiniz başarıyla oluşturuldu. Operasyondaki Gönderilerim sayfasından barkod yazdırma ve operasyona iletme işlemini yapabilirsiniz.`}
                extra={[
                  <Button
                    type="primary"
                    key="console"
                    onClick={() => history.push("/deliveries/operation")}
                  >
                    Operasyondaki Gönderilerim
                  </Button>,
                  info.authorities.includes("PRINT_BARCODE") && (
                    <Button
                      key="print"
                      onClick={() => handlePrint()}
                      type="dashed"
                    >
                      Barkod Yazdır
                    </Button>
                  ),
                  <Button
                    key="buy"
                    onClick={() => {
                      onClose();
                    }}
                  >
                    Kapat
                  </Button>,
                ].filter(Boolean)}
              />
            </>
          ) : (
            <>
              <Table
                pagination={false}
                columns={columns}
                dataSource={state}
                scroll={{ y: 400 }}
                bordered
                rowKey="barcode"
              />
              {succesBarcodes.length > 0 &&
                info.authorities.includes("PRINT_BARCODE") && (
                  <div style={{ textAlign: "center", marginTop: 16 }}>
                    <Button
                      key="print"
                      onClick={() => handlePrint()}
                      type="dashed"
                    >
                      Barkod Yazdır
                    </Button>
                  </div>
                )}
            </>
          )}
        </div>
      </Modal>
    </>
  );
};

export default PackageResultModal;
